import type { ICustomerQuery, IOrdersEdgesNode, IOrdersLineItemsEdges } from "@repo/shopify/src/queries/customer-gql";
import type { Cart, Filter } from "@shopify/hydrogen-react/storefront-api-types";

/**
 * @description ShopifyId를 넣으면 {objectName, identifier} 형태로 return
 */
export const extractId = (shopifyId: string): string => {
  if (!shopifyId) return "";
  return shopifyId.split("?")[0].split("/").slice(-1).pop() ?? "";
};

/**
 * @description ShopifyId 2개가 같은지 비교
 */
export const matchExtractedId = (id1: string, id2: string): boolean => {
  if (!id1 || !id2) return false;
  return extractId(id1) === extractId(id2);
};

/**
 * @description Collection에서 총 상품 갯수 가져오기
 */
export const getTotalProductCount = (filters: Filter[]) => {
  if (!filters.length) return 0;

  const count = filters.find((filter) => filter.id.includes("hidden"));
  if (!count) return 0;

  return count.values.filter(({ label }) => label === "아니요" || label === "No")[0]?.count;
};

/**
 * @description 계산된 잔여수량 가져오기
 * user의 orders를 순회하며, quantityLimit이 있는 상품의 잔여 수량을 계산한다.
 * Redux Store에 저장된 user를 파라미터로 받는다.
 */
export type TAdjustCartLines = { quantity: number; merchandiseId: string }[];

/**
 * @description 지난 주문 목록을 보고 남은 quantityLimit을 Map으로 저장
 */
export const getQuantityLimitObj = ({ customer }: ICustomerQuery | { customer: undefined }): Record<string, number> => {
  return customer
    ? customer.orders.edges
        .filter(({ node }: { node: IOrdersEdgesNode }) => node.isfamilysale)
        .filter(({ node }: { node: IOrdersEdgesNode }) => node.exclude_limit?.value !== "true" && !node.cancelReason)
        .reduce<Record<string, number>>((accumulator, { node }) => {
          node.lineItems.edges
            .filter(({ node: itemNode }: IOrdersLineItemsEdges) => itemNode.variant)
            .forEach(({ node: lineItemsNode }: IOrdersLineItemsEdges) => {
              const product = lineItemsNode.variant?.product;
              if (product && product.quantityLimit) {
                if (!(product.id in accumulator)) {
                  accumulator[product.id] = parseInt(product.quantityLimit.value);
                }

                accumulator[product.id] -= lineItemsNode.quantity;
              }
            });

          return accumulator;
        }, {})
    : {};
};

/**
 * @description 조정된 lineItems를 반환
 * cart의 lineItems를 순회하며, quantityLimitMap에 따라 quantity를 조정
 * Redux Store에 저장된 cart와 getQuantityLimit 함수가 만든 quantityLimitMap을 파라미터로 받는다
 */
export const adjustCartLines = (cart: Cart, quantityLimitMap: Record<string, number>): TAdjustCartLines => {
  return cart.lines.edges
    .map((line) => {
      let quantity = line.node.quantity;
      if (line.node.merchandise.product.id in quantityLimitMap) {
        const capacity = quantityLimitMap[line.node.merchandise.product.id];
        if (capacity === 0) {
          quantity = 0;
          // capacity보다 적게 담긴 경우에는
          // 1. quantity 유지
          // 2. capaticy 줄여주기
        } else if (quantity <= capacity) {
          quantityLimitMap[line.node.merchandise.product.id] = capacity - quantity;
          // capaticy보다 많이 담긴 경우
          // 1. quantity를 capacity만큼으로 바꿔주고
          // 2. capacity는 0으로 내려준다
        } else {
          quantity = capacity;
          quantityLimitMap[line.node.merchandise.product.id] = 0;
        }
      }

      // 사용 가능한 재고보다 많이 담은 경우에 조정
      if (line.node.merchandise.quantityAvailable && line.node.merchandise.quantityAvailable < quantity) {
        quantity = line.node.merchandise.quantityAvailable;
      }

      return { quantity, merchandiseId: line.node.merchandise.id };
    })
    .filter((line) => line.quantity > 0);
};

/**
 * @description 두 lines를 비교하여 같으면 true, 다르면 false를 반환
 * Redux Store에 저장된 cart의 cart.lines.edges와 adjustCartLines 함수가 만든 lines를 파라미터로 받는다
 */
export const linesEqual = (cart: Cart, linesAdjusted: TAdjustCartLines): boolean => {
  const filteredLines = cart.lines.edges
    .map((line) => ({
      quantity: line.node.quantity,
      merchandiseId: line.node.merchandise.id,
    }))
    .sort((a, b) => parseInt(extractId(a.merchandiseId), 10) - parseInt(extractId(b.merchandiseId), 10));

  linesAdjusted.sort((a, b) => parseInt(extractId(a.merchandiseId), 10) - parseInt(extractId(b.merchandiseId), 10));

  return JSON.stringify(filteredLines) === JSON.stringify(linesAdjusted);
};

/**
 * @description 상품별 갯수 제한 확인
 */
export type TAdjustCartProduct = {
  quantity: number;
  merchandiseId: string;
  productId: string;
}[];

/**
 * @description 지난 주문 목록을 보고 남은 quantityLimit을 Map으로 저장
 */
export const getQuantityLimitProduct = ({ customer }: ICustomerQuery): Record<string, number> => {
  if (customer.orders.edges.length === 0) {
    return {};
  }

  return customer.orders.edges
    .filter(({ node }: { node: IOrdersEdgesNode }) => node.isfamilysale)
    .filter(({ node }: { node: IOrdersEdgesNode }) => node.exclude_limit?.value !== "true" && !node.cancelReason)
    .reduce<Record<string, number>>((accumulator, { node }) => {
      node.lineItems.edges
        .filter(({ node: itemNode }: IOrdersLineItemsEdges) => itemNode.variant)
        .forEach(({ node: lineItemsNode }: IOrdersLineItemsEdges) => {
          const product = lineItemsNode.variant?.product;
          if (product && product.quantityLimitProduct) {
            if (!(product.id in accumulator)) {
              accumulator[product.id] = parseInt(product.quantityLimitProduct.value);
            }

            accumulator[product.id] -= lineItemsNode.quantity;
          }
        });

      return accumulator;
    }, {});
};

/**
 * @description 조정된 lineItems를 반환
 * cart의 lineItems를 순회하며, quantityLimitMap에 따라 quantity를 조정
 * Redux Store에 저장된 cart와 getQuantityLimit 함수가 만든 quantityLimitMap을 파라미터로 받는다
 */
export const adjustCartProduct = (cart: Cart, quantityLimitMap: Record<string, number>): TAdjustCartProduct => {
  return cart.lines.edges
    .map((line) => {
      let quantity = line.node.quantity;
      if (line.node.merchandise.product.id in quantityLimitMap) {
        const capacity = quantityLimitMap[line.node.merchandise.product.id];
        if (capacity === 0) {
          quantity = 0;
          // capacity보다 적게 담긴 경우에는
          // 1. quantity 유지
          // 2. capaticy 줄여주기
        } else if (quantity <= capacity) {
          quantityLimitMap[line.node.merchandise.product.id] = capacity - quantity;
          // capaticy보다 많이 담긴 경우
          // 1. quantity를 capacity만큼으로 바꿔주고
          // 2. capacity는 0으로 내려준다
        } else {
          quantity = capacity;
          quantityLimitMap[line.node.merchandise.product.id] = 0;
        }
      }

      // 사용 가능한 재고보다 많이 담은 경우에 조정
      if (line.node.merchandise.quantityAvailable && line.node.merchandise.quantityAvailable < quantity) {
        quantity = line.node.merchandise.quantityAvailable;
      }

      return {
        quantity,
        merchandiseId: line.node.merchandise.id,
        productId: line.node.merchandise.product.id,
      };
    })
    .filter((line) => line.quantity > 0);
};

export const linesEqualProduct = (cart: Cart, linesAdjusted: TAdjustCartProduct): boolean => {
  const filteredLines = cart.lines.edges
    .map((line) => ({
      quantity: line.node.quantity,
      merchandiseId: line.node.merchandise.id,
      productId: line.node.merchandise.product.id,
    }))
    .sort((a, b) => parseInt(extractId(a.productId), 10) - parseInt(extractId(b.productId), 10));

  linesAdjusted.sort((a, b) => parseInt(extractId(a.productId), 10) - parseInt(extractId(b.productId), 10));

  return JSON.stringify(filteredLines) === JSON.stringify(linesAdjusted);
};
